import apiClient from './client';
import type { Occupation } from './career.api';

// ─── Typen ──────────────────────────────────────────────────────────────────

export interface Person {
  id: string;
  title: string | null;
  firstName: string;
  lastName: string | null;
  birthName: string | null;
  gender: string | null;
  birthDateText: string | null;
  birthDate: string | null;
  birthPlace: string | null;
  deathDateText: string | null;
  deathDate: string | null;
  deathPlace: string | null;
  occupation: string | null;
  notes: string | null;
  isLiving: boolean;
  profileMediaId: string | null;
  profileMedia: { id: string; filename: string } | null;
  createdAt: string;
  updatedAt: string;
}

/** Person mit allen Relationen (GET /persons/:id) */
export interface PersonDetail extends Person {
  sources: Array<{
    id: string;
    title: string | null;
    author: string | null;
    /** Fundstelle/Signatur (Archiv, Band, Seite). */
    publication: string | null;
    /** Datum der Quelle selbst, ISO-String oder null. */
    date: string | null;
    url: string | null;
    notes: string | null;
    /** Fundort innerhalb der Quelle, gilt nur für diese Person. */
    linkNotes: string | null;
    /** Wie viele Personen diese Quelle insgesamt belegt. */
    linkedPersonCount: number;
    /** Verknüpftes Dokument im Archiv. */
    media: { id: string; filename: string; originalName: string | null; mimeType: string | null } | null;
  }>;
  familyAsChild: Array<{
    id: string;
    parent1: { id: string; firstName: string; lastName: string | null; gender: string | null } | null;
    parent2: { id: string; firstName: string; lastName: string | null; gender: string | null } | null;
    relationship: { id: string; type: string } | null;
    relationType: string;
  }>;
  children: Array<{
    id: string;
    child: { id: string; firstName: string; lastName: string | null; gender: string | null; birthDateText: string | null };
    parent1: { id: string; firstName: string; lastName: string | null } | null;
    parent2: { id: string; firstName: string; lastName: string | null } | null;
  }>;
  partnerships: Array<{
    id: string;
    type: string;
    partner: { id: string; firstName: string; lastName: string | null; gender: string | null };
    startDate: string | null;
    endDate: string | null;
  }>;
  /** Belege je Angabe (Geburtsdatum, Ort …) mit Sicherheitsgrad. */
  factCitations?: Array<{
    id: string;
    fact: string;
    sourceId: string | null;
    locator: string | null;
    confidence: string;
    notes: string | null;
    source: { id: string; title: string | null; author: string | null; publication: string | null } | null;
  }>;
  /** Berufsstationen, chronologisch sortiert (optional, bis alle Responses sie liefern). */
  occupations?: Occupation[];
  /** Marker, ob ein Lebenslauf-Text existiert (nur id + updatedAt, kein Text). */
  biography?: { id: string; updatedAt: string } | null;
}

export interface SearchParams {
  page?: number;
  limit?: number;
  q?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

// `null` leert ein Feld beim PATCH; `undefined` lässt es unverändert.
export interface CreatePersonDto {
  title?: string | null;
  first_name: string;
  last_name?: string | null;
  birth_name?: string | null;
  gender?: 'male' | 'female' | 'other' | 'unknown';
  birth_date_text?: string | null;
  birth_place?: string | null;
  death_date_text?: string | null;
  death_place?: string | null;
  occupation?: string | null;
  notes?: string | null;
  is_living?: boolean;
}

export type UpdatePersonDto = Partial<CreatePersonDto> & {
  /** Profilbild setzen (Media-ID) oder entfernen (null) */
  profile_media_id?: string | null;
};

// ─── API-Funktionen ─────────────────────────────────────────────────────────

/** Personenliste mit Paginierung & Suche */
export async function getPersons(params: SearchParams = {}): Promise<PaginatedResponse<Person>> {
  const { data } = await apiClient.get<PaginatedResponse<Person>>('/persons', { params });
  return data;
}

/** Einzelne Person mit allen Relationen */
export async function getPerson(id: string): Promise<PersonDetail> {
  const { data } = await apiClient.get<PersonDetail>(`/persons/${id}`);
  return data;
}

/** Neue Person anlegen */
export async function createPerson(dto: CreatePersonDto): Promise<Person> {
  const { data } = await apiClient.post<Person>('/persons', dto);
  return data;
}

/** Person aktualisieren */
export async function updatePerson(id: string, dto: UpdatePersonDto): Promise<Person> {
  const { data } = await apiClient.patch<Person>(`/persons/${id}`, dto);
  return data;
}

/** Person löschen */
export async function deletePerson(id: string): Promise<void> {
  await apiClient.delete(`/persons/${id}`);
}

export interface OriginEntry {
  label: string;
  percentage: number;
  weight: number;
}

export interface OriginsResponse {
  origins: OriginEntry[];
  totalAncestors: number;
}

/** Herkunftsverteilung einer Person nach Ländern */
export async function getPersonOrigins(id: string): Promise<OriginsResponse> {
  const { data } = await apiClient.get<OriginsResponse>(`/persons/${id}/origins`);
  return data;
}
